import React, { useState, useEffect } from 'react';
import { X, Check, Calendar, Clock, Users, Layers, ChevronRight } from 'lucide-react';

const TaskForm = ({ 
  departments, 
  teamMembers, 
  selectedProject,
  projectsData,
  onCreateTask,
  onCancel
}) => {
  const [step, setStep] = useState(1);
  const [animating, setAnimating] = useState(false);
  const [newTask, setNewTask] = useState({
    name: '',
    department: '',
    status: 'not-started',
    progress: 0,
    assignee: '',
    dueDate: '',
    project: ''
  });
  
  // Find project ID
  useEffect(() => {
    if (selectedProject && projectsData && projectsData.length > 0) {
      const projectRecord = projectsData.find(p => p.fields.Name === selectedProject);
      if (projectRecord) {
        setNewTask(prev => ({ ...prev, project: projectRecord.id }));
      }
    }
  }, [selectedProject, projectsData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreateTask(newTask);
  };
  
  const nextStep = () => {
    setAnimating(true);
    setTimeout(() => {
      setStep(prev => prev + 1);
      setAnimating(false);
    }, 300);
  };
  
  const prevStep = () => {
    setAnimating(true);
    setTimeout(() => {
      setStep(prev => prev - 1);
      setAnimating(false);
    }, 300);
  };
  
  const updateProgress = (value) => {
    let status = newTask.status;
    if (value === 0) status = 'not-started';
    else if (value === 100) status = 'completed';
    else status = 'in-progress';
    
    setNewTask({ ...newTask, progress: value, status });
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30 backdrop-blur-sm transition-all duration-300">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl transform transition-all duration-300 overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-8 py-6 border-b border-gray-100">
          <h2 className="text-xl font-medium text-gray-800">
            {step === 1 ? 'New Task' : step === 2 ? 'Task Details' : 'Task Progress'}
          </h2>
          <button 
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Progress indicator */}
        <div className="px-8 pt-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                1
              </div>
              <div className={`h-1 w-10 mx-1 ${step > 1 ? 'bg-green-500' : 'bg-gray-200'}`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                2
              </div>
              <div className={`h-1 w-10 mx-1 ${step > 2 ? 'bg-green-500' : 'bg-gray-200'}`}></div>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                3
              </div>
            </div>
            <span className="text-sm text-gray-500">Step {step} of 3</span>
          </div>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className={`transition-all duration-300 transform ${animating ? 'opacity-0 translate-x-6' : 'opacity-100 translate-x-0'}`}>
            {/* Step 1: Basic Info */}
            {step === 1 && (
              <div className="px-8 py-6">
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Task Name</label>
                  <input 
                    type="text" 
                    className="w-full p-3 text-base border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-green-400 focus:outline-none transition-all"
                    value={newTask.name}
                    onChange={(e) => setNewTask({...newTask, name: e.target.value})}
                    placeholder="What needs to be done?"
                    required
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                  <div className="relative">
                    <select 
                      className="w-full p-3 text-base appearance-none border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-green-400 focus:outline-none transition-all pl-12"
                      value={newTask.department}
                      onChange={(e) => setNewTask({...newTask, department: e.target.value})}
                      required
                    >
                      <option value="">Select Department</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.fields.Name}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Layers size={20} className="text-gray-400" />
                    </div>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronRight size={16} className="text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 2: Assignment & Dates */}
            {step === 2 && (
              <div className="px-8 py-6">
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assignee</label>
                  <div className="relative">
                    <select 
                      className="w-full p-3 text-base appearance-none border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-green-400 focus:outline-none transition-all pl-12"
                      value={newTask.assignee}
                      onChange={(e) => setNewTask({...newTask, assignee: e.target.value})}
                      required
                    >
                      <option value="">Select Team Member</option>
                      {teamMembers.map(member => (
                        <option key={member.id} value={member.id}>{member.fields.Name}</option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Users size={20} className="text-gray-400" />
                    </div>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronRight size={16} className="text-gray-400" />
                    </div>
                  </div>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                  <div className="relative">
                    <input 
                      type="date" 
                      className="w-full p-3 text-base border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-green-400 focus:outline-none transition-all pl-12"
                      value={newTask.dueDate}
                      onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
                      required
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar size={20} className="text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 3: Status & Progress */}
            {step === 3 && (
              <div className="px-8 py-6">
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <div className="flex space-x-4">
                    <button 
                      type="button"
                      className={`flex-1 py-3 px-4 rounded-lg transition-all ${newTask.status === 'not-started' ? 'bg-gray-100 border-2 border-gray-300' : 'bg-white border border-gray-200'}`}
                      onClick={() => setNewTask({...newTask, status: 'not-started', progress: 0})}
                    >
                      <div className="flex items-center justify-center">
                        <Clock size={18} className="mr-2 text-gray-500" />
                        <span>Not Started</span>
                      </div>
                    </button>
                    <button 
                      type="button"
                      className={`flex-1 py-3 px-4 rounded-lg transition-all ${newTask.status === 'in-progress' ? 'bg-blue-50 border-2 border-blue-300' : 'bg-white border border-gray-200'}`}
                      onClick={() => setNewTask({...newTask, status: 'in-progress', progress: 50})}
                    >
                      <div className="flex items-center justify-center">
                        <Clock size={18} className="mr-2 text-blue-500" />
                        <span>In Progress</span>
                      </div>
                    </button>
                    <button 
                      type="button"
                      className={`flex-1 py-3 px-4 rounded-lg transition-all ${newTask.status === 'completed' ? 'bg-green-50 border-2 border-green-300' : 'bg-white border border-gray-200'}`}
                      onClick={() => setNewTask({...newTask, status: 'completed', progress: 100})}
                    >
                      <div className="flex items-center justify-center">
                        <Check size={18} className="mr-2 text-green-500" />
                        <span>Completed</span>
                      </div>
                    </button>
                  </div>
                </div>
                
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">Progress</label>
                    <span className="text-sm font-medium text-gray-700">{newTask.progress}%</span>
                  </div>
                  <div className="relative">
                    <input 
                      type="range" 
                      min="0" 
                      max="100" 
                      step="5"
                      value={newTask.progress}
                      onChange={(e) => updateProgress(parseInt(e.target.value))}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500"
                    />
                    <div 
                      className="absolute top-0 left-0 h-3 bg-green-500 rounded-lg pointer-events-none transition-all duration-300" 
                      style={{ width: `${newTask.progress}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between mt-1 text-xs text-gray-500">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 mb-4">
                  <h3 className="font-medium text-gray-800 mb-2">Task Summary</h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div>
                      <span className="text-gray-500">Task:</span> 
                      <span className="ml-2 text-gray-800">{newTask.name || 'Not specified'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Department:</span> 
                      <span className="ml-2 text-gray-800">
                        {departments.find(d => d.id === newTask.department)?.fields.Name || 'Not specified'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Assignee:</span> 
                      <span className="ml-2 text-gray-800">
                        {teamMembers.find(t => t.id === newTask.assignee)?.fields.Name || 'Not specified'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Due Date:</span> 
                      <span className="ml-2 text-gray-800">
                        {newTask.dueDate ? new Date(newTask.dueDate).toLocaleDateString() : 'Not specified'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Footer with actions */}
          <div className="px-8 py-5 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
            {step > 1 ? (
              <button 
                type="button"
                className="px-5 py-2 bg-white text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                onClick={prevStep}
              >
                Back
              </button>
            ) : (
              <button 
                type="button"
                className="px-5 py-2 bg-white text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                onClick={onCancel}
              >
                Cancel
              </button>
            )}
            
            {step < 3 ? (
              <button 
                type="button"
                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={nextStep}
                disabled={
                  (step === 1 && (!newTask.name || !newTask.department)) ||
                  (step === 2 && (!newTask.assignee || !newTask.dueDate))
                }
              >
                Continue
              </button>
            ) : (
              <button 
                type="submit"
                className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
              >
                <Check size={18} className="mr-2" />
                Create Task
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskForm;
